/**
 * Reading an attachment's bytes, through a frame on Cloud.
 *
 * Relay's web page never holds a vault key, and must not start to, so it
 * cannot decrypt a file itself. A share link carries its own key, though, and
 * Cloud's `/bridge?for=relay` is a hidden page that opens one exactly as the
 * public share page does. This page frames it, once, the first time anything
 * asks; posts it `{id, linkId, secret, maxBytes}`; and takes back
 * `{id, ok: true, bytes, mime, name}` or `{id, ok: false, reason}`.
 *
 * A reply counts only when it comes from Cloud's origin *and* from the frame
 * this page made, in that shape, for a request still waiting. Anything else is
 * dropped without a word. A request posts only to Cloud's origin, so a frame
 * that has been navigated anywhere else is handed nothing. A request this page
 * gives up on is called off with `{id, cancel: true}`, so the frame stops
 * fetching for it.
 *
 * Cloud's side is `website-cloud/web/src/bridge.ts`. Not one of the four files
 * the CLI vendors from this directory.
 */

import { cloudOrigin } from './cloud-picker';

export interface Link {
  id: string;
  secret: string;
}

export type BridgeReply =
  | { id: number; ok: true; bytes: ArrayBuffer; mime: string; name: string }
  | { id: number; ok: false; reason: string };

/** What a frame needs to be: something a message can be posted to. */
export interface FrameWindow {
  postMessage(message: unknown, targetOrigin: string): void;
}

/** The page around the bridge: a way to make the frame, and to hear it. */
export interface FrameHost {
  /** Make the frame at `src`; resolves with its window once it has loaded. */
  open(src: string): Promise<FrameWindow>;
  listen(handler: (event: { origin: string; source: unknown; data: unknown }) => void): void;
}

export interface Bridge {
  /** The file behind `link`, refused past `maxBytes`. Rejects on any refusal. */
  open(link: Link, maxBytes: number): Promise<{ bytes: ArrayBuffer; mime: string; name: string }>;
}

// Cloud's refusals. Anything else it says is read as a plain failure.
const REASONS = new Set(['password', 'capped', 'too-large', 'failed']);

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * A reply from `expected.source` on `expected.origin`, or null for anything
 * else. Null too while there is no frame to have sent one.
 */
export function readBridgeReply(
  event: { origin: string; source: unknown; data: unknown },
  expected: { origin: string; source: unknown }
): BridgeReply | null {
  if (event.origin !== expected.origin) return null;
  if (!expected.source || event.source !== expected.source) return null;

  const data = event.data;
  if (!isObject(data)) return null;
  const { id, ok } = data;
  if (typeof id !== 'number' || !Number.isSafeInteger(id) || id < 1) return null;

  if (ok === true) {
    const { bytes, mime, name } = data;
    if (!(bytes instanceof ArrayBuffer) || typeof mime !== 'string' || typeof name !== 'string') return null;
    // A fresh object, so nothing else the sender put beside these is read.
    return { id, ok: true, bytes, mime, name };
  }
  if (ok === false) {
    const reason = data['reason'];
    return { id, ok: false, reason: typeof reason === 'string' && REASONS.has(reason) ? reason : 'failed' };
  }
  return null;
}

/**
 * How long to wait for one file: twenty seconds, and as long again as the
 * largest file allowed would take at 250 kB/s. Past it the request is called
 * off, and a reply that comes anyway is ignored.
 */
export const timeoutFor = (maxBytes: number) => 20_000 + Math.ceil(maxBytes / 250);

export function createBridge(origin: string, host: FrameHost): Bridge {
  type Waiting = {
    resolve: (reply: { bytes: ArrayBuffer; mime: string; name: string }) => void;
    reject: (err: Error) => void;
    timer: ReturnType<typeof setTimeout>;
  };

  let frame: Promise<FrameWindow> | null = null;
  let source: FrameWindow | null = null;
  let next = 0;
  const waiting = new Map<number, Waiting>();

  host.listen((event) => {
    const reply = readBridgeReply(event, { origin, source });
    if (!reply) return;
    const request = waiting.get(reply.id);
    if (!request) return;
    waiting.delete(reply.id);
    clearTimeout(request.timer);
    if (reply.ok) request.resolve({ bytes: reply.bytes, mime: reply.mime, name: reply.name });
    else request.reject(new Error(reply.reason));
  });

  return {
    open(link, maxBytes) {
      const id = ++next;
      return new Promise((resolve, reject) => {
        let asked: FrameWindow | null = null;
        const timer = setTimeout(() => {
          waiting.delete(id);
          asked?.postMessage({ id, cancel: true }, origin);
          reject(new Error('timeout'));
        }, timeoutFor(maxBytes));
        waiting.set(id, { resolve, reject, timer });

        frame ??= host.open(`${origin}/bridge?for=relay`).then((w) => (source = w));
        void frame.then((target) => {
          if (!waiting.has(id)) return;
          target.postMessage({ id, linkId: link.id, secret: link.secret, maxBytes }, origin);
          asked = target;
        });
      });
    }
  };
}

/** The hidden frame, in this document. */
export const documentHost: FrameHost = {
  open: (src) =>
    new Promise((resolve) => {
      const frame = document.createElement('iframe');
      // Its own scripts on its own origin, and nothing else: no popups, no
      // navigating this page, no forms. It draws nothing and takes no focus.
      frame.setAttribute('sandbox', 'allow-scripts allow-same-origin');
      // Chromium gives a credentialless frame its own throwaway storage, so
      // the bridge, though on Cloud's origin, cannot reach the IndexedDB a
      // trusted device keeps its vault key in. Other browsers ignore it.
      frame.setAttribute('credentialless', '');
      frame.setAttribute('aria-hidden', 'true');
      frame.tabIndex = -1;
      frame.style.display = 'none';
      frame.addEventListener('load', () => resolve(frame.contentWindow as FrameWindow), { once: true });
      frame.src = src;
      document.body.appendChild(frame);
    }),
  listen: (handler) => window.addEventListener('message', handler)
};

let shared: Bridge | null = null;

/** This page's bridge, to the Cloud beside this Relay. Made on first use. */
export function cloudBridge(): Bridge {
  return (shared ??= createBridge(cloudOrigin(), documentHost));
}
